import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DefaultError } from "@tanstack/query-core";
import { enqueueSnackbar } from "notistack";
import {Button, CircularProgress, Stack} from "@mui/material";
import { TextInput } from "../form/TextInput";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { createTicket, updateStatus } from "../../service/ticket.service";
import { SupportTicket } from "../../types/SupportTicket";

interface SupportTicketFormProps {
    parent?: string,
    entityId?: string,
    onSubmit?: (ticket?: SupportTicket) => void,
    setLoading?: (value: boolean) => void,
    defaultTitle?: string,
    hideTitle?: boolean//should be renamed to "isNew"
    status?: "OPEN" | "CLOSED"
    margin: string
}

export function SupportTicketForm({
    status,
                                      parent,
                                      entityId,
                                      onSubmit,
                                      setLoading,
                                      defaultTitle,
                                      hideTitle,
                                      margin
                                  }: SupportTicketFormProps) {
    const queryClient = useQueryClient();
    const [submitHappen, setSubmitHappen] = useState(false);

    const validationSchema = useMemo(() =>
        Yup.object({
            title: hideTitle ? Yup.string().notRequired() : Yup.string()
                .min(3, "Title must be at least 3 characters")
                .required("Title is required"),
            content: Yup.string()
                .min(5, "At least 5 characters")
                .required("This field is required"),
        }), [hideTitle]);

    const initialValues = {
        title: defaultTitle,
        content: "",
    }

    const formik: FormikProps<Partial<SupportTicket>> = useFormik<Partial<SupportTicket>>({
        initialValues: initialValues,
        validationSchema,
        onSubmit: (values) => {
            mutation.mutate(values);
        },
    });

    const mutation = useMutation<SupportTicket, DefaultError, Partial<SupportTicket>>({
        mutationFn: (payload) => createTicket({
            ...payload,
            parentId: parent,
            entityId,
            entityType: "PROPOSAL"
        }).then(r => r.data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["tickets"] })
                .then(() => {
                    if(!hideTitle) {
                        enqueueSnackbar({ message: "Ticket created successfully", variant: "success" });
                    }
                    if (onSubmit) onSubmit(response);
                    formik.resetForm({values: initialValues});
                });
        },
        onError: (error) => {
            enqueueSnackbar({ message: "Failed to create ticket: " + error.message, variant: "error" });
        }
    });

    const changeStatusMutation = useMutation<SupportTicket, DefaultError, void>({
        mutationFn: () => updateStatus(parent!, {
            status: status === 'OPEN' ? 'CLOSED' : 'OPEN',
        }).then((response) => {
            formik.setFieldValue("status", response.data.status);
            return response.data
        }),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ["tickets"] })
            if (onSubmit) onSubmit(response);
        },
        onError: (error) => {
            enqueueSnackbar({ message: "Failed to update ticket status: " + error.message, variant: "error" });
        }
    });

    useEffect(() => {
        setLoading && setLoading(formik.isSubmitting || mutation.isPending);
    }, [setLoading, formik.isSubmitting, mutation.isPending]);

    return <form style={{ width: "100%", maxWidth: 1200, margin: margin}} onSubmit={(e) => {
        setSubmitHappen(true);
        formik.handleSubmit(e);
    }}>
        <Stack flexDirection={"column"} gap={"15px"} className={"mb-15"}>
            {!hideTitle && <TextInput
                submitHappen={submitHappen}
                formik={formik}
                name={"title"}
                label={"Title"}
            />}
            <TextInput
                submitHappen={submitHappen}
                formik={formik}
                name={"content"}
                label={"Let us know what you need help with"}
                multiline
                rows={4}
            />
        </Stack>

        <Stack flexDirection={"row"} justifyContent={"space-between"}>
            <Stack flexDirection={"row"} gap={"20px"}>
                {onSubmit && <Button color="inherit" variant={"contained"} onClick={() => onSubmit()}>{hideTitle ? "Back" : "Close"}</Button>}
                {!mutation.isPending && <Button type="submit" variant="contained" color="primary">{hideTitle ? "Submit New Comment" : "Create Ticket"}</Button>}
                {mutation.isPending && <CircularProgress/>}
            </Stack>
            {hideTitle && status && parent && <Button variant={"contained"} color={"primary"} onClick={() => changeStatusMutation.mutate()}>
                {status === 'OPEN' ? "Close" : "Reopen"}
            </Button>}
        </Stack>
    </form>
}